import { ref } from 'vue';
import { SERVER_GENERAL_ERROR_MESSAGE } from 'Config/constants';
import { RequestError } from 'Api';
import { messageSuccess, messageInfo, messageError, messageHide } from 'Helpers/message';
import { FormBeforeSendNoChangesError, FormBeforeSendValidationError, FormBeforeSendError } from './errors';
import { IFormOptions } from './types';

function getNormalizedOptions(options: IFormOptions) {
  const defaultOptions = {
    resetOnSuccess: false,
    messageSuccess: '',
    scrollTopOnSuccess: true,
    scrollToErrorFieldOnValidation: true,
    changedDataRequired: true,
    ignoreValidation: false,
    messageOnValidationError: 'One or more fields are invalid',
  };

  return {
    ...defaultOptions,
    ...options,
  };
}

export function useForm(formOptions: IFormOptions) {
  const options = getNormalizedOptions(formOptions);
  const isFormSendSuccess = ref(false);
  const isFormLoading = ref(false);
  const formSendErrorMessage = ref('');
  let toasterMessageId = '';

  /**
   * Hide a toast message if it is shown in any reason
   */
  function hideToastMessage() {
    messageHide(toasterMessageId);
  }

  /**
   * Show a toast message on a form successfully send if a message is set in options
   */
  async function showToastMessageOnSuccess() {
    if (options.messageSuccess) {
      if (Array.isArray(options.messageSuccess)) {
        toasterMessageId = await messageInfo(options.messageSuccess);
      } else {
        toasterMessageId = await messageSuccess(options.messageSuccess);
      }
    }
  }

  /**
   * Check if form is valid. Throw an error if not.
   * Message of error can be custom by setting messageOnValidationError in options
   */
  async function formCheckValidation() {
    // if form validation is not set, skip validation
    if (!options.formValidation) return;
    // if form validation is set, validate it
    const isValid = await options.formValidation.value.$validate();
    // if a form is not valid
    if (!isValid) {
      // touch all fields to show errors
      options.formValidation.value.$touch();
      // throw error. Pass custom message, or leave it empty to use default
      throw new FormBeforeSendValidationError(options.messageOnValidationError || undefined);
    }
  }

  /**
   * Check if form is changed. Throw an error if not.
   */
  function formCheckNoChangeValidation() {
    if (!options.formModel.value.isChanged) {
      throw new FormBeforeSendNoChangesError();
    }
  }

  /**
   * Check before form send. Throw an error if form is not valid or not changed
   */
  async function formCheckBeforeSend() {
    try {
      // If ignoreValidation is set, skip validation. Or validate form
      if (!options.ignoreValidation) await formCheckValidation();
      // If changedDataRequired is set, check if form is changed. Or skip check
      if (options.changedDataRequired) formCheckNoChangeValidation();
    } catch (error) {
      // If error is FormBeforeSendError,
      if (error instanceof FormBeforeSendError) {
        // show a message from error
        toasterMessageId = await messageError(error.message);
      }
      throw error;
    }
  }

  /**
   * Do preparation and validation before form send
   */
  async function formBeforeSend() {
    // Hide a toast message if it is shown in any reason
    hideToastMessage();
    // Reset form error message (common it is a server error from previous send)
    formSendErrorMessage.value = '';
    // Check if form is valid and changed
    await formCheckBeforeSend();
    isFormLoading.value = true;
  }

  function reset() {
    options.formModel.value.resetFields();
    options.formValidation?.value?.$reset();
  }

  /**
   * Do actions after form successfully send
   */
  async function formOnSendSuccess() {
    // Set flag that form is successfully send
    isFormSendSuccess.value = true;
    if (options.resetOnSuccess) reset();
    // Show a toast message on a form successfully send
    await showToastMessageOnSuccess();
  }

  /**
   * Do actions after form send error
   * @param error
   */
  async function formOnSendError(error?: RequestError) {
    // If no error, or server error
    if (error === undefined || error?.response?.status >= 500) {
      // show a general error message
      toasterMessageId = await messageError(SERVER_GENERAL_ERROR_MESSAGE);
    } else {
      // If showServerErrorAsToast is set
      if (options.showServerErrorAsToast && error.message) {
        // show a server error message
        toasterMessageId = await messageError(error.message);
      }
      // Set form error message
      formSendErrorMessage.value = error.message;
    }
  }

  function onFormAlways() {
    isFormLoading.value = false;
  }

  /**
   * Send a form
   */
  async function formSend() {
    await formBeforeSend();
    try {
      const result = await options.formModel.value.send();
      formOnSendSuccess();
      return result;
    } catch (error) {
      formOnSendError(error as RequestError);
      throw error;
    } finally {
      onFormAlways();
    }
  }
  return {
    FormBeforeSendError,
    formValidation: options.formValidation,
    $v: options.formValidation,
    isFormSendSuccess,
    formCheckValidation,
    formCheckNoChangeValidation,
    formCheckBeforeSend,
    formSendErrorMessage,
    isFormLoading,
    formSend,
  };
}
